import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Users } from 'lucide-react';

interface Supervisor {
  user_id: string;
  email: string;
}

interface BulkPatientAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPatientIds: string[];
  onSuccess: () => void;
}

export function BulkPatientAssignmentDialog({
  open,
  onOpenChange,
  selectedPatientIds,
  onSuccess,
}: BulkPatientAssignmentDialogProps) {
  const { user } = useAdminAuth();
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingSupervisors, setLoadingSupervisors] = useState(true);

  useEffect(() => {
    if (open) {
      loadSupervisors();
      setSelectedSupervisor('');
    }
  }, [open, user?.agency_id]);

  const loadSupervisors = async () => {
    if (!user?.agency_id) return;

    setLoadingSupervisors(true);
    try {
      // Get all users with supervisor role in the agency
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, email')
        .eq('agency_id', user.agency_id)
        .eq('is_active', true);

      // Get supervisor roles
      const { data: roles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'supervisor');

      const supervisorUserIds = new Set(roles?.map(r => r.user_id) || []);
      const supervisorList = (profiles || [])
        .filter(p => supervisorUserIds.has(p.user_id))
        .map(p => ({
          user_id: p.user_id,
          email: p.email,
        }));

      setSupervisors(supervisorList);
    } catch (error) {
      console.error('Error loading supervisors:', error);
      toast.error('Failed to load supervisors');
    } finally {
      setLoadingSupervisors(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedSupervisor || selectedPatientIds.length === 0) return;

    setLoading(true);
    try {
      // Get existing assignments for this supervisor
      const { data: existingAssignments } = await supabase
        .from('patient_assignments')
        .select('patient_id')
        .eq('staff_user_id', selectedSupervisor)
        .is('revoked_at', null);

      const existingPatientIds = new Set(existingAssignments?.map(a => a.patient_id) || []);
      
      // Filter out patients already assigned
      const newPatientIds = selectedPatientIds.filter(id => !existingPatientIds.has(id));

      if (newPatientIds.length === 0) {
        toast.info('All selected patients are already assigned to this supervisor');
        onOpenChange(false);
        return;
      }

      // Create new assignments
      const assignments = newPatientIds.map(patientId => ({
        patient_id: patientId,
        staff_user_id: selectedSupervisor,
        assigned_by: user?.id || null,
      }));

      const { error } = await supabase
        .from('patient_assignments')
        .insert(assignments);

      if (error) throw error;

      const skipped = selectedPatientIds.length - newPatientIds.length;
      const message = skipped > 0
        ? `Assigned ${newPatientIds.length} patients (${skipped} already assigned)`
        : `Assigned ${newPatientIds.length} patients successfully`;

      toast.success(message);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error assigning patients:', error);
      toast.error('Failed to assign patients');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Assign Patients to Supervisor
          </DialogTitle>
          <DialogDescription>
            Assign {selectedPatientIds.length} selected patient{selectedPatientIds.length !== 1 ? 's' : ''} to a supervisor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            <Users className="h-4 w-4" />
            <span>{selectedPatientIds.length} patient{selectedPatientIds.length !== 1 ? 's' : ''} selected</span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supervisor">Select Supervisor</Label>
            <Select
              value={selectedSupervisor}
              onValueChange={setSelectedSupervisor}
              disabled={loadingSupervisors}
            >
              <SelectTrigger id="supervisor">
                <SelectValue placeholder={loadingSupervisors ? 'Loading...' : 'Choose a supervisor'} />
              </SelectTrigger>
              <SelectContent>
                {supervisors.map((supervisor) => (
                  <SelectItem key={supervisor.user_id} value={supervisor.user_id}>
                    {supervisor.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!loadingSupervisors && supervisors.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No supervisors found. Add a supervisor first.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedSupervisor || loading || supervisors.length === 0}
          >
            {loading ? 'Assigning...' : 'Assign Patients'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
